import { useSnackbar } from 'notistack';
import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';

import { IconPlus } from '@tabler/icons-react';

import CustomModal from '@/components/CustomModal';
import Input from '@/components/Input/Input';
import Select from '@/components/Select';
import { ERoleAccess } from '@/enum';
import useAccess from '@/hooks/useAccess';
import { useFormatText } from '@/hooks/useFormatText';
import { addAdminSchema, TAddAdminFormValues } from '@/schema/addAdminSchema';
import { createAdmin, getRoleDropdown } from '@/service/adminServices';
import { zodResolver } from '@hookform/resolvers/zod';

const AddAdminModal = ({
  setIsOpen,
  onSubmit,
}: {
  setIsOpen: (isOpen: boolean) => void;
  onSubmit?: () => void;
}) => {
  const { hasAccess } = useAccess();

  const [isLoading, setIsLoading] = useState(false);
  const [roleOptions, setRoleOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const { formatText } = useFormatText();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await getRoleDropdown();
      if (response?.status && response?.data) {
        const formattedRoles = response.data?.map(
          (role: { id: string; name: string }) => ({
            value: role.id,
            label: role.name,
          })
        );
        setRoleOptions(formattedRoles);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      enqueueSnackbar('Failed to load roles', { variant: 'error' });
    }
  };

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<TAddAdminFormValues>({
    resolver: zodResolver(addAdminSchema),
    defaultValues: {
      name: '',
      email: '',
      role: '',
    },
  });

  const handleOnSubmit = async (data: TAddAdminFormValues) => {
    setIsLoading(true);
    try {
      const payload = {
        name: data.name,
        email: data.email,
        role_id: data.role,
      };
      const response = await createAdmin(payload);

      if (response?.status) {
        enqueueSnackbar(response?.message || 'Admin created successfully', {
          variant: 'success',
        });
        setIsOpen(false);
        onSubmit?.();
        reset();
      } else {
        enqueueSnackbar(response?.message || 'Failed to create admin', {
          variant: 'error',
        });
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('An error occurred while creating admin', {
        variant: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!hasAccess(ERoleAccess.USER_MANAGEMENT)) return null;

  return (
    <CustomModal
      setIsOpen={setIsOpen}
      className="w-150 max-w-[90vw]"
      isFooter
      isHeader
      heading={formatText('user-management.admin.add-admin')}
      position="right"
      onClose={reset}
      cancelButtonProps={{
        label: formatText('common.actions.cancel'),
        desructive: true,
        onClick: () => {
          setIsOpen(false);
        },
      }}
      confirmButtonProps={{
        label: formatText('user-management.admin.add-admin'),
        variant: 'primary',
        icon: <IconPlus />,
        onClick: handleSubmit(handleOnSubmit),
        loading: isLoading,
      }}
    >
      <div className="flex flex-col gap-10 w-full">
        <Input
          label={formatText('common.field.name')}
          name="name"
          placeholder="Mr. Fahad Ali"
          register={register}
          error={
            errors.name?.message ? formatText(errors.name.message) : undefined
          }
          inputClassName="py-4! min-h-13"
          isRequired
        />

        <Input
          label={formatText('common.field.email')}
          name="email"
          placeholder="fahad.ali@alphascholl.com"
          register={register}
          inputClassName="py-4! min-h-13"
          error={
            errors.email?.message ? formatText(errors.email.message) : undefined
          }
          isRequired
        />

        <Controller
          name="role"
          control={control}
          render={({ field }) => (
            <Select
              name={formatText('user-management.admin.column.role')}
              options={roleOptions}
              selected={field.value || ''}
              onChange={field.onChange}
              placeholder={formatText('common.select')}
              isTextFormat
              isRequired
              isClearable
              className="py-4! min-h-13"
              error={
                errors.role?.message
                  ? formatText(errors.role.message)
                  : undefined
              }
            />
          )}
        />
      </div>
    </CustomModal>
  );
};

export default AddAdminModal;
